import { GET_UPLOAD_TEMPORARY_ACCESS_MUTATION, type UploadTemporaryAccessOutput } from 'graphql/s3/S3Mutations';
import { ApolloError, MutationFunctionOptions, useMutation } from '@apollo/client';
import { ExecutionResult } from 'graphql';
import S3Upload from 'react-s3-uploader/s3upload';
import { dataURLtoBlob } from 'utils/image';
import { useIntl, type IntlShape } from 'react-intl';
import {type ShowSnackbarFunction} from 'hooks';
import CryptoJS from "crypto-js";

type GetUploadTemporaryAccessThenUploadType = UseUploadFileToS3Param & {
  contentType: string;
  dataURL: string;
  intl: IntlShape,
  getUploadTemporaryAccessMutation: (
    options?:
      | MutationFunctionOptions<
          UploadTemporaryAccessOutput,
          Record<string, any>
        >
      | undefined,
  ) => Promise<ExecutionResult<UploadTemporaryAccessOutput>>;
};

export type S3UploadOutputType = {
  contentType: string;
  dataURL: string;
  key: string;
  s3Url: string;
};

const getUploadTemporaryAccessThenUpload = async ({
  contentType,
  dataURL,
  getUploadTemporaryAccessMutation,
  handleProgress,
  errorSnackbar,
  intl
}: GetUploadTemporaryAccessThenUploadType): Promise<S3UploadOutputType> => {
  return new Promise((resolve, reject) => {
    const file = dataURLtoBlob(dataURL, contentType);
    const reader = new FileReader();

    reader.readAsArrayBuffer(file);
    reader.onload = event => {
      const result = event?.target?.result;
      const binary = CryptoJS.lib.WordArray.create(result);
      const md5 = CryptoJS.MD5(binary);
      const checksum = md5.toString(CryptoJS.enc.Base64);      
      getUploadTemporaryAccessMutation({
        variables: {
          checksum: checksum,
          contentType: file.type,
          contentLength: file.size,
        },
      })
      .then(({ data }) => {
        if (data) {
          const { key, url } = data.getUploadTemporaryAccess;
          return new S3Upload({
            files: [file],
            getSignedUrl: (_f: File, callback: Function) => {
              callback({ signedUrl: url });
            },
            uploadRequestHeaders: {
              'Content-MD5': checksum,
              'Cache-Control': 'no-cache',
            },
            onFinishS3Put: (): void =>
              resolve({
                contentType,
                dataURL,
                key,
                s3Url: url,
              }),
            onProgress: handleProgress,
            onError: (status: string): void => {
              errorSnackbar(intl.formatMessage({ id: 'error.uploadError' }));
              reject(status);
            },
          });
        } else {
          errorSnackbar(intl.formatMessage({ id: 'error.uploadError' }));
          reject('Response data is empty');
        }
      })
      .catch((error) => {
        errorSnackbar(intl.formatMessage({ id: 'error.uploadError' }));
        reject(error);
      });
    };
  });
};

type UseUploadFileToS3Param = {
  handleProgress?: Function;
  errorSnackbar: ShowSnackbarFunction;
};

export type UploadFileToS3 = (
  dataURL: string,
  contentType: string,
) => Promise<S3UploadOutputType>;

type UseUploadFileToS3Return = {
  getUploadTemporaryAccessStatus: {
    error: ApolloError | undefined;
    loading: boolean;
  };
  uploadFileToS3: UploadFileToS3;
};

export const useUploadFileToS3 = (props: UseUploadFileToS3Param): UseUploadFileToS3Return => {
  const [getUploadTemporaryAccessMutation, { error, loading }] =
    useMutation<UploadTemporaryAccessOutput>(GET_UPLOAD_TEMPORARY_ACCESS_MUTATION);

  const intl = useIntl();

  const uploadFileToS3 = (
    dataURL: string,
    contentType: string,
  ): Promise<S3UploadOutputType> =>
    getUploadTemporaryAccessThenUpload({
      contentType,
      dataURL,
      getUploadTemporaryAccessMutation,
      intl,
      ...props,
    });

  return { getUploadTemporaryAccessStatus: { error, loading }, uploadFileToS3 };
};

export default useUploadFileToS3;